import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, Target, Flame, Award } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Estadisticas() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="h-8 bg-white/5 rounded w-48 mb-8"></div>
        <div className="grid grid-cols-2 gap-6 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-white/5 rounded-xl"></div>
          ))}
        </div>
        <div className="h-64 bg-white/5 rounded-xl"></div>
      </div>
    );
  }

  if (!stats || stats.total_sessions === 0) {
    return (
      <div className="p-8">
        <h1 className="text-3xl font-bold text-white mb-8">Estadísticas</h1>
        <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-12 text-center">
          <Award className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-white mb-2">Sin datos suficientes</h2>
          <p className="text-white/50">Completa al menos 5 sesiones para ver tus estadísticas</p>
        </div>
      </div>
    );
  }

  // Generate z-score history (simulated based on chart_data)
  const zScoreData = stats.chart_data?.map((d, i) => ({
    label: d.date,
    z: ((d.hit - 50) / 50 * Math.sqrt(i + 1)).toFixed(2)
  })) || [];

  // Streak data
  const streakData = [
    { label: 'Racha actual', value: stats.current_streak },
    { label: 'Mejor racha', value: stats.best_streak }
  ];

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-white mb-8">Estadísticas</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div className="kpi-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(160,84%,39%)]/20 flex items-center justify-center">
              <Target className="w-5 h-5 text-[hsl(160,84%,50%)]" />
            </div>
            <span className="text-sm text-white/70">Tasa de aciertos</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats.hit_rate}%</p>
          <p className="text-xs text-white/40 mt-1">
            {stats.hits} de {stats.total_sessions}
          </p>
        </div>

        <div className="kpi-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(252,37%,60%)]/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-[hsl(252,37%,70%)]" />
            </div>
            <span className="text-sm text-white/70">Z-score</span>
          </div>
          <p className="text-3xl font-bold text-white">
            {stats.z_score > 0 ? '+' : ''}{stats.z_score}
          </p>
          <p className="text-xs text-white/40 mt-1">
            {stats.z_score >= 4 ? '¡Excepcional!' : stats.z_score >= 2 ? 'Significativo' : 'Sigue practicando'}
          </p>
        </div>

        <div className="kpi-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(33,92%,55%)]/20 flex items-center justify-center">
              <Flame className="w-5 h-5 text-[hsl(33,92%,60%)]" />
            </div>
            <span className="text-sm text-white/70">Racha actual</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats.current_streak}</p>
          <p className="text-xs text-white/40 mt-1">Aciertos consecutivos</p>
        </div>

        <div className="kpi-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[hsl(198,62%,54%)]/20 flex items-center justify-center">
              <Award className="w-5 h-5 text-[hsl(198,62%,60%)]" />
            </div>
            <span className="text-sm text-white/70">Mejor racha</span>
          </div>
          <p className="text-3xl font-bold text-white">{stats.best_streak}</p>
          <p className="text-xs text-white/40 mt-1">Récord personal</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Hit Rate Chart */}
        <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Evolución de tasa de aciertos</h3>
          <div className="h-64" data-testid="hit-rate-chart">
            {stats.chart_data?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.chart_data}>
                  <XAxis dataKey="date" stroke="#7f8ea3" fontSize={12} />
                  <YAxis domain={[0, 100]} stroke="#7f8ea3" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(232, 36%, 12%)', 
                      border: '1px solid hsl(232, 18%, 22%)',
                      borderRadius: '8px'
                    }}
                  />
                  <ReferenceLine y={50} stroke="#64748b" strokeDasharray="4 4" />
                  <Line 
                    type="monotone" 
                    dataKey="hit" 
                    stroke="hsl(198, 62%, 54%)" 
                    strokeWidth={2} 
                    dot={{ fill: 'hsl(198, 62%, 54%)', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-white/40">
                Sin datos suficientes
              </div>
            )}
          </div>
        </div>

        {/* Z-Score Chart */}
        <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Evolución del Z-score</h3>
          <div className="h-64" data-testid="zscore-chart">
            {zScoreData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={zScoreData}>
                  <XAxis dataKey="label" stroke="#7f8ea3" fontSize={12} />
                  <YAxis stroke="#7f8ea3" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(232, 36%, 12%)', 
                      border: '1px solid hsl(232, 18%, 22%)',
                      borderRadius: '8px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="z" 
                    stroke="hsl(252, 37%, 60%)" 
                    fill="hsl(252, 37%, 60%)" 
                    fillOpacity={0.2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-white/40">
                Sin datos suficientes
              </div>
            )}
          </div>
        </div>

        {/* Streaks Chart */}
        <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6 lg:col-span-2">
          <h3 className="text-lg font-medium text-white mb-4">Comparación de rachas</h3>
          <div className="h-48" data-testid="streaks-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={streakData} layout="vertical">
                <XAxis type="number" stroke="#7f8ea3" fontSize={12} />
                <YAxis dataKey="label" type="category" stroke="#7f8ea3" fontSize={12} width={100} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'hsl(232, 36%, 12%)', 
                    border: '1px solid hsl(232, 18%, 22%)',
                    borderRadius: '8px'
                  }}
                />
                <Bar 
                  dataKey="value" 
                  fill="hsl(198, 62%, 54%)" 
                  radius={[0, 6, 6, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Interpretation Guide */}
      <div className="mt-8 bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl p-6">
        <h3 className="text-lg font-medium text-white mb-4">Guía de interpretación</h3>
        <div className="grid md:grid-cols-3 gap-6 text-sm">
          <div>
            <h4 className="font-medium text-[hsl(160,84%,50%)] mb-2">Z-score ≥ 2.0</h4>
            <p className="text-white/60">Estadísticamente significativo. Probabilidad menor al 5% de ser azar.</p>
          </div>
          <div>
            <h4 className="font-medium text-[hsl(252,37%,70%)] mb-2">Z-score ≥ 4.0</h4>
            <p className="text-white/60">Altamente significativo (como Kolodziejzyk). Menos de 0.003% de ser azar.</p>
          </div>
          <div>
            <h4 className="font-medium text-[hsl(198,62%,60%)] mb-2">Tasa > 52%</h4>
            <p className="text-white/60">Con suficientes sesiones, puede generar retorno positivo en trading real.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
